import { useCallback, useEffect, useState } from 'react';

import { Input, Label } from '@iwoca/orion';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import styles from './ChooseLoanAmount.module.css';
import { intlFormatCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { TrackAmountSliderUsed } from '../../../../tracking/amplitude.helpers';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import { Slider } from '../Slider';

type TChooseLoanAmountProps = {
    className?: string;
    updateDrawdownAmount: (drawdownAmount: number) => void;
    drawdownAmount: number;
    minimumDrawdownAmount: number;
    maximumDrawdownAmount: number;
    inputTooltip?: React.ReactNode;
    label?: { id: string; defaultMessage: string };
    setNewCalculationRequired?: (newCalculationRequired: boolean) => void;
    showSlider?: boolean;
};

export const ChooseLoanAmount = ({
    className,
    updateDrawdownAmount,
    drawdownAmount,
    minimumDrawdownAmount,
    maximumDrawdownAmount,
    inputTooltip,
    label = defineMessage({
        id: '_.choose_loan_amount.withdrawal_amount_header',
        defaultMessage: 'I want to borrow {input}'
    }),
    showSlider = true
}: TChooseLoanAmountProps) => {
    const intl = useIntl();
    const [sliderAmount, _setSliderAmount] = useState(drawdownAmount);

    const calculateTextInputValueFromSliderAmount = useCallback(
        (sliderAmount: number) => {
            return intlFormatCurrency({
                intl,
                value: sliderAmount
            });
        },
        [intl]
    );

    const [textInputValue, setTextInputValue] = useState(
        calculateTextInputValueFromSliderAmount(sliderAmount)
    );

    const setSliderAmount = useCallback(
        (value: number) => {
            _setSliderAmount(value);
            setTextInputValue(calculateTextInputValueFromSliderAmount(value));
        },
        [
            _setSliderAmount,
            setTextInputValue,
            calculateTextInputValueFromSliderAmount
        ]
    );

    useEffect(() => {
        setSliderAmount(drawdownAmount);
    }, [drawdownAmount, setSliderAmount]);

    return (
        <div className={className}>
            <div className={styles.withdrawal}>
                <Label htmlFor="drawdown-amount-input">
                    <FormattedMessage
                        {...label}
                        values={{
                            input: (
                                // eslint-disable-next-line iwoca/input-a11y
                                <Input
                                    id="drawdown-amount-input"
                                    data-testid="drawdown-amount-input"
                                    wrapperClassName={
                                        styles.withdrawalAmountInput
                                    }
                                    value={textInputValue}
                                    onChange={(event) => {
                                        setTextInputValue(
                                            event.currentTarget.value
                                        );
                                    }}
                                    onBlur={(event) => {
                                        const value = event.target.value;
                                        const numberString = value.replace(
                                            /[^0-9]+/g,
                                            ''
                                        );
                                        let amountNumber =
                                            parseInt(numberString, 10) ||
                                            minimumDrawdownAmount;

                                        if (
                                            amountNumber < minimumDrawdownAmount
                                        ) {
                                            amountNumber =
                                                minimumDrawdownAmount;
                                        }

                                        if (
                                            amountNumber > maximumDrawdownAmount
                                        ) {
                                            amountNumber =
                                                maximumDrawdownAmount;
                                        }
                                        setSliderAmount(amountNumber);
                                        updateDrawdownAmount(amountNumber);
                                    }}
                                />
                            )
                        }}
                    ></FormattedMessage>
                    {inputTooltip}
                </Label>
            </div>
            {showSlider && (
                <div className={styles.sliderContainer}>
                    <Slider
                        minValueLabel={
                            <div data-testid="minimum-drawdown-amount">
                                <MoneyNoDecimals
                                    value={minimumDrawdownAmount}
                                />
                            </div>
                        }
                        maxValueLabel={
                            <div
                                data-testid="maximum-drawdown-amount"
                                className={styles.sliderMax}
                            >
                                <MoneyWithDecimals
                                    value={maximumDrawdownAmount}
                                />
                            </div>
                        }
                        id="drawdown-amount-slider"
                        name="drawdown-amount-slider"
                        sliderMax={maximumDrawdownAmount}
                        sliderMin={minimumDrawdownAmount}
                        value={sliderAmount}
                        handleChange={(value) =>
                            setSliderAmount(
                                parseInt(value) > maximumDrawdownAmount
                                    ? maximumDrawdownAmount
                                    : parseInt(value)
                            )
                        }
                        step={50}
                        onAfterChange={() => {
                            updateDrawdownAmount(sliderAmount);
                            // @ts-expect-error: mismatched types
                            TrackAmountSliderUsed({
                                'Slider amount': sliderAmount
                            });
                        }}
                        data-action-name="Loan Amount slider"
                        data-testid="drawdown-amount-slider"
                    />
                </div>
            )}
        </div>
    );
};
